import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import './App.css';

// Contexts
import { AuthProvider } from './contexts/AuthContext';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import AboutPage from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import UserApprovalPage from './pages/UserApprovalPage';
import AccountPage from './pages/AccountPage';
import KB2ChatPage from './pages/KB2ChatPage';
import AdminPage from './pages/AdminPage';

// Auth component
import ProtectedRoute from './components/auth/ProtectedRoute';

// Debug components
import { createLogger } from './utils/logger';

// Create logger instance for App
const logger = createLogger('App');

// Create theme with dark mode and colors matching the Streamlit app
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#68c8de', // Blue color matching the Streamlit app's primaryColor
      light: '#9fdbeb',
      dark: '#3b97b1',
      contrastText: '#000000',
    },
    secondary: {
      main: '#26A69A', // Teal accent
      light: '#64D8CB',
      dark: '#00766C',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0e1117', // Dark gray background matching Streamlit
      paper: '#121212',   // Slightly lighter black for paper elements
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 500,
    },
    h4: {
      fontWeight: 500,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#121212',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: '#68c8de',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#4ba9bf',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E1E1E',
        }
      }
    }
  },
});

function App() {
  // Log application startup
  logger.info('Application starting');
  logger.info(`Environment: ${process.env.NODE_ENV}`);
  logger.info(`API URL: ${process.env.REACT_APP_API_URL || 'Using proxy'}`);
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
          <Router>
            <Routes>
              {/* Routes for non-authenticated users only */}
              <Route element={<ProtectedRoute requireAuth={false} />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              </Route>
              
              {/* Special routes for auth flows */}
              <Route path="/confirm-email" element={<EmailConfirmationPage />} />
              <Route path="/approve-user" element={<UserApprovalPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              {/* Main layout with nested routes */}
              <Route element={<MainLayout />}>
                {/* Public routes */}
                <Route path="/" element={<AboutPage />} />
                
                {/* Protected routes (require authentication) */}
                <Route element={<ProtectedRoute requireAuth={true} />}>
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/chat" element={<Navigate to="/chat-kb2" replace />} />
                  <Route path="/chat-kb2" element={<KB2ChatPage />} />
                  <Route path="/analyse" element={<Navigate to="/chat-kb2" replace />} />
                  <Route path="/write" element={<Navigate to="/chat-kb2" replace />} />
                  <Route path="/xdi" element={<Navigate to="/chat-kb2" replace />} />
                  <Route path="/workflow" element={<Navigate to="/chat-kb2" replace />} />
                </Route>
                
                {/* Admin routes (require authentication and admin privileges) */}
                <Route element={<ProtectedRoute requireAuth={true} requireAdmin={true} />}>
                  <Route path="/admin" element={<AdminPage />} />
                </Route>
                
                {/* Default redirect for unknown routes */}
                <Route path="*" element={<Navigate to="/" />} />
              </Route>
            </Routes>
          </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
